import { useEffect } from 'react'
import { useLocation, useNavigate } from 'router-v6'
import { ACI_RETURN_TO_KEY } from 'v2/pages/settings/integrations/nango'
import { SuspenseLoader } from 'v2/shared/components/suspense-loader'
import { useIntegrationRollout } from 'v2/shared/hooks/use-integration-rollout'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'
import { errorToast, successToast } from 'v2/utils/toasts'
import {
  parseAciOAuthCompletion,
  resolveOAuthCallbackSource,
  safeReturnTo,
} from './aci-oauth-landing-utils'
import { useConfirmAciConnectionMutation, useFinishLegacyAciConnectMutation } from './queries'

interface AciOAuthLandingDependencies {
  aciEnabled: boolean
  confirmAciConnection: (ownerId: string) => Promise<RExternalOrgApp | undefined>
  finishLegacyConnection: (orgAppUuid: string) => Promise<void>
  notifySuccess: typeof successToast
  notifyError: typeof errorToast
  logError: (message: string) => void
}

interface AciOAuthLandingProps {
  dependencies?: AciOAuthLandingDependencies
}

const defaultLogError = (message: string) => logger.error(message)

/** Handles both the canonical ACI callback and the disabled-rollout legacy callback. */
export const AciOAuthLanding = ({ dependencies }: AciOAuthLandingProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const rollout = useIntegrationRollout()
  const { mutateAsync: confirmAciConnection } = useConfirmAciConnectionMutation()
  const { mutateAsync: finishLegacyAciConnect } = useFinishLegacyAciConnectMutation()
  const aciEnabled = dependencies?.aciEnabled ?? rollout.useAciIntegrations
  const isResolved = dependencies ? true : rollout.isResolved
  const confirmConnection = dependencies?.confirmAciConnection ?? confirmAciConnection
  const finishLegacyConnection = dependencies?.finishLegacyConnection ?? finishLegacyAciConnect
  const notifySuccess = dependencies?.notifySuccess ?? successToast
  const notifyError = dependencies?.notifyError ?? errorToast
  const logError = dependencies?.logError ?? defaultLogError
  // Depend on the primitive, not a field read off the mutable location object
  // (react-doctor/no-mutable-in-deps).
  const locationSearch = location.search

  useEffect(() => {
    if (!isResolved) return
    let cancelled = false
    const returnTo = safeReturnTo(sessionStorage.getItem(ACI_RETURN_TO_KEY))

    const done = () => {
      if (cancelled) return
      sessionStorage.removeItem(ACI_RETURN_TO_KEY)
      navigate(returnTo)
    }

    const completeAciConnect = async () => {
      const completion = parseAciOAuthCompletion(locationSearch)

      if (completion.kind === 'cancelled') {
        notifyError({
          title: 'Connection cancelled',
          description: 'Authorization was cancelled. You can reconnect when you are ready.',
        })
        done()
        return
      }

      if (completion.kind === 'error') {
        notifyError({
          title: 'Connection error',
          description: `Authorization did not complete (${completion.errorCode}). Please reconnect.`,
        })
        done()
        return
      }

      try {
        const match = await confirmConnection(completion.ownerId)
        if (cancelled) return
        if (match) {
          notifySuccess({
            title: 'Connected',
            description: `${match.name} connected successfully.`,
          })
        } else {
          notifyError({
            title: 'Connection error',
            description: 'The connection was not found. Please reconnect.',
          })
        }
      } catch {
        if (cancelled) return
        logError('ACI bridge connect confirmation failed')
        notifyError({
          title: 'Connection error',
          description: 'Could not confirm the connection. Please try again.',
        })
      } finally {
        done()
      }
    }

    const finishLegacyConnect = async () => {
      const params = new URLSearchParams(locationSearch)
      const orgAppUuid = params.get('org_app_uuid')
      const appName = params.get('app_name') || 'App'

      try {
        if (!orgAppUuid) {
          notifyError({
            title: 'Connection error',
            description: 'Missing connection reference.',
          })
          return
        }
        await finishLegacyConnection(orgAppUuid)
        if (cancelled) return
        notifySuccess({
          title: 'Connected',
          description: `${appName} connected successfully.`,
        })
      } catch {
        if (cancelled) return
        logError('ACI finish-connect failed')
        notifyError({
          title: 'Connection error',
          description: 'Could not finalize the connection.',
        })
      } finally {
        done()
      }
    }

    if (resolveOAuthCallbackSource(locationSearch, aciEnabled) === 'aci') {
      completeAciConnect()
    } else {
      finishLegacyConnect()
    }

    return () => {
      cancelled = true
    }
  }, [
    aciEnabled,
    confirmConnection,
    finishLegacyConnection,
    isResolved,
    locationSearch,
    logError,
    navigate,
    notifyError,
    notifySuccess,
  ])

  return <SuspenseLoader text="Finishing your connection..." />
}
